import { mergeInventoryMetadata } from './inventory-shared-catalog';
import type { FromBasePlayerInput, FromBaseRow as Row } from './from-base-context';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { createFromBasePlayerGrantContext } from './from-base-player-grants';
import { constructPlayerSaveSectionsFromBase } from './from-base-player-sections';
import { createCreatureEquippedCountsCompiler } from './creature-equipped-counts';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid, readItemUuidSlot } from './inventory-raw-readers';
import { meetsAbilityLevelRequirements } from './ability-level-requirement-gate';
import { compilePlSignatures, createPlSignatureJsonSources } from './pl-signature';
import { constructNpcCastableFromBase } from './from-base-npc-castable';
import { normalizeFromBaseCatalog } from './from-base-catalog-normalization';
import { phpJsonInt, phpJsonString } from './php-json-values';
import { constructPlayerActionBarsFromBase } from './from-base-player-display';
import { relocateCreatureDerivedToComputed } from './creature-derived-relocation';
import { playerSpellKnownPointers, createActorAbilityMaterializer } from './from-base-player-abilities';
import type { FormulaVariableReaders } from './formula-variable-resolution';

const extraAbilities = ['feat', 'natural_weapon', 'charged_spell', 'charged_ability', 'at_will_spell',
    'innate_spell_per_long_rest', 'status_effect_permanent', 'natural_armor'];

/** Shared final ability projection over an owned rule window, including every
 * grant bucket and the native class-level filter. No display or catalog walk. */
export function constructPlayerAbilityClustersFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, host: Row): void {
    const context = createFromBasePlayerGrantContext(base, metadata, host, 'catalog-lean-normalization');
    const { readers, common, definition } = context;
    const stored = (row: Row, key: string) => readers.itemField({ [key]: [row[key] ?? null] }, key);
    const entries = (row: Row, key: string) => Object.values(readers.layerEntries(row, key));
    const creature = (host.computed as Row).creature as Row;
    const abilities: Row = {};
    for (const key of [...metadata.abilityTypes, ...extraAbilities]) {
        const rows = entries(host, key);
        if (rows.length) abilities[key] = rows;
    }
    creature.abilities = abilities;
    const providers = createCatalogDefaultProviderReader({ ...common, nodeItemId: readNodeItemId });
    const pc = host.player_characters as Row, levels: Record<string, number> = {};
    for (let slot = 1; slot <= 20; slot++) for (const key of [`class_${slot}`, `subclass_${slot}`]) {
        const id = readItemUuidSlot(readers.itemField(pc, key));
        if (id) levels[id.toLowerCase()] = phpJsonInt(readers.itemField(pc, `class_level_${slot}`));
    }
    for (const key of [...metadata.abilityTypes, 'feat']) {
        if (!Array.isArray(abilities[key])) continue;
        const carrierOf = new Map<string, string>();
        for (const carrier of providers.providers(host)) for (const entry of Object.values(readers.layerEntries(carrier, key))) {
            const id = readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition }).toLowerCase();
            if (id && !carrierOf.has(id)) carrierOf.set(id, readNodeItemId(carrier) ?? '');
        }
        abilities[key] = (abilities[key] as unknown[]).filter(entry => {
            if (fromBaseRecord(entry) && (stored(entry, 'ability_id') !== null || stored(entry, 'item_arr') !== null)) return true;
            const id = readLayerListEntryUuid(entry), source = carrierOf.get(id.toLowerCase());
            if (!source) return true;
            const row = fromBaseRecord(entry) && Object.keys(entry.item_layer_types as Row ?? {}).length ? entry : definition(id);
            if (!row) return true;
            const requirement = (key: string) => readers.layerValue(row, key) == null || readers.layerValue(row, key) === '' ? null : phpJsonInt(readers.layerValue(row, key));
            return meetsAbilityLevelRequirements({ isPlayerCharacter: true, totalLevel: phpJsonInt(readers.layerValue(host, 'total_level')),
                sourceClassLevel: levels[source.toLowerCase()] ?? null,
                requiresLevel: requirement('requires_level'), requiresClassLevel: requirement('requires_class_level') });
        });
    }
    // Attach counts after the winning lists and level gates settle. An early
    // computed mirror would hide later finalization edits and enter the PL signature.
    const { materialize } = createActorAbilityMaterializer(base, metadata, host, context);
    for (const key of metadata.abilityTypes) {
        if (!Array.isArray(abilities[key])) continue;
        const rows = abilities[key] as unknown[];
        const carried = new Set(rows.filter(fromBaseRecord).map(entry =>
            readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition }).toUpperCase()));
        abilities[key] = rows.map(entry => {
            const held = fromBaseRecord(entry) ? stored(entry, 'charges') : null;
            if (fromBaseRecord(held) && stored(held, 'current') !== null) return entry;
            const id = readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition });
            // Catalog normalization retires a bare ref when its rich own entry
            // is already carried. Keep that ref bare so hydration cannot duplicate it.
            if (typeof entry === 'string' && carried.has(id.toUpperCase())) return entry;
            const ability = fromBaseUuid(id) ? definition(id) : null;
            if (!ability || readers.layerValue(ability, 'ability_charges') === null) return entry;
            return { ...(fromBaseRecord(entry) ? entry : { item_id: id }), charges: materialize(entry, id, ability).charges };
        });
    }
    for (const key of ['spells_known', 'curses_known']) if (Array.isArray(host[key]) && (host[key] as unknown[]).length) creature[key] = host[key];
    (creature.meta as Row).total_level = phpJsonInt(readers.layerValue(host, 'total_level'));
}

/** The same post-relocation spell view is used by entry compile and form deltas.
 * Root grant buckets have already moved to computed.creature at this point. */
export function constructPlayerSpellClustersFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, host: Row): void {
    const context = createFromBasePlayerGrantContext(base, metadata, host, 'catalog-lean-normalization');
    const { readers } = context;
    const definition = (id: string): Row | null => { const row = context.definition(id); return fromBaseRecord(row) ? row : null; };
    constructNpcCastableFromBase(base as unknown as Row, metadata, host, { readers, definition, common: { resolveCarrierRef: definition } });
    const creature = (host.computed as Row).creature as Row;
    for (const key of ['spells_known', 'curses_known']) if (Array.isArray(creature[key]))
        creature[key] = playerSpellKnownPointers(creature[key] as Row[], host.player_characters as Row, readers, definition);
}

/** Native lean value pass, reusable over only the affected rule clusters. */
export function normalizePlayerClusterValues(input: Row | unknown[], readers: FormulaVariableReaders,
    shell: (id: string) => Row | null): Row | unknown[] {
    const shellKeys = new Set(['team', 'preferred_speed_type', 'ancestry_choice_1', 'ancestry_choice_2', 'ancestry_choice_3',
        'class_mechanic', 'lifestyle', 'encounter_weather', 'encounter_environment', 'encounter_tempurature', 'encounter_wind', 'voice_id']);
    const strip = new Set(['starting_equipment_item_id', 'picture_private', 'picture_deleted', 'video_private', 'video_deleted', 'video_created_by_id', 'video_updated_by_id']);
    function walk(node: Row | unknown[]): Row | unknown[] {
        let out = node;
        const writable = (): Row => {
            if (out === node) out = Array.isArray(node) ? node.slice() : { ...node };
            return out as Row;
        };
        for (const [key, value] of Object.entries(node)) {
            if (strip.has(key) || value === null) { delete writable()[key]; continue; }
            if (/^(requires_)?skill_tier_\d+$/.test(key) && value !== null && typeof value === 'object') {
                writable()[key] = Object.values(value).map(entry => fromBaseRecord(entry) && fromBaseUuid(readers.itemField(entry, 'item_id'))
                    ? { items: Object.fromEntries(['item_id', 'item_name', 'item_type'].map(name => [name, readers.itemField(entry, name)]).filter(([, value]) => value !== null)) } : entry);
                continue;
            }
            if (shellKeys.has(key) && fromBaseUuid(value)) {
                const ref = shell(value);
                if (ref) writable()[key] = ref;
            } else if (value !== null && typeof value === 'object') {
                const child = walk(value as Row);
                if (child !== value) writable()[key] = child;
            }
        }
        return out;
    }
    return walk(input);
}

/** Native PC reshape after the final rule clusters: source nests, ability
 * mirrors, spell pointers, castable payload and the single root catalog pass. */
export function normalizePlayerFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerResourceMetadata, input: Row): Row {
    let host = fromBaseClone(input);
    delete host._from_base_providers;
    const playerSource = host.player_characters as Row;
    const late = ['fortune_input_default', 'spirit_input_default'];
    host.player_characters = Object.fromEntries([...Object.keys(playerSource).filter(key => !late.includes(key)), ...late]
        .filter(key => Object.prototype.hasOwnProperty.call(playerSource, key)).map(key => [key, playerSource[key]]));
    const computedSource = host.computed as Row;
    host.computed = Object.fromEntries([...new Set(['vitality', 'creature', '_cache', ...Object.keys(computedSource)])]
        .filter(key => Object.prototype.hasOwnProperty.call(computedSource, key)).map(key => [key, computedSource[key]]));
    const context = createFromBasePlayerGrantContext(base, metadata, host, 'catalog-lean-normalization');
    const { readers, deps } = context;
    const definition = (id: string): Row | null => { const row = context.definition(id); return fromBaseRecord(row) ? row : null; };
    const inventory = mergeInventoryMetadata(metadata.inventoryMetadata!, base.inventory_metadata);
    const rawCatalog: Record<string, Row> = { ...metadata.stageSources, ...base.catalog as Readonly<Record<string, Row>> };
    const shell = (id: string): Row | null => {
        const row = inventory.shellRows?.[id] ?? rawCatalog[id] ?? null;
        return row ? { items: Object.fromEntries(['item_id', 'item_name', 'item_type'].map(key => [key, readers.itemField(row, key)])) } : null;
    };
    const signature = base.pl_signature ?? inventory.plSignature;
    if (!signature) throw new FromBaseStageMissing('catalog-lean-normalization', 'player-pl-signature-input');
    host = Object.fromEntries([...new Set(['encounter_creatures', 'encounter_creatures_spells_prepared', 'position', 'computed', 'player_characters',
        ...Object.keys(constructPlayerActionBarsFromBase(base)), 'items', 'item_layer_types', '_user_choice_nested_map', ...Object.keys(host)])]
        .filter(key => Object.prototype.hasOwnProperty.call(host, key)).map(key => [key, host[key]]));
    const computed = host.computed as Row, creature = computed.creature as Row;
    computed._cache = { ...computed._cache as Row, ...compilePlSignatures(host, signature,
        createPlSignatureJsonSources({ ...metadata.stageSources, ...base.catalog }, inventory.definitionAssets?.vfxRows ?? [], inventory.shellRegistries)) };
    computed.pl = null; computed.pl_pending = 1;
    constructPlayerAbilityClustersFromBase(base, metadata, host);
    host.user_choices = base.user_choices.map(raw => {
        const row = fromBaseClone(raw);
        for (const key of ['user_choice_i', 'user_choice_t', 'user_choice_t_updated', 'user_choice_private',
            'user_choice_deleted', 'user_choice_created_by_id', 'user_choice_updated_by_id']) delete row[key];
        for (const key of ['choice_item_id', 'exact_choice_item_id', 'choice']) {
            const id = readItemUuidSlot(readers.itemField(row, key)), ref = id ? shell(id) : null;
            if (ref) row[`${key}_shell`] = ref;
        }
        return row;
    });
    host = createCreatureEquippedCountsCompiler({ ...deps, ...metadata.equippedCounts })(host) as Row;
    const finalCreature = (host.computed as Row).creature as Row;
    relocateCreatureDerivedToComputed(host, readers);
    finalCreature.weight ??= {};
    if (Object.prototype.hasOwnProperty.call(host, 'spell_casting_level')) {
        (finalCreature.spellcasting as Row).spell_casting_level = phpJsonInt(readers.layerValue(host, 'spell_casting_level')); delete host.spell_casting_level;
    }
    host = constructPlayerSaveSectionsFromBase(base, metadata, host);
    const taxonomy = Object.fromEntries(Object.entries(host.item_layer_types as Row ?? {}).filter(([key]) => ['creature_type', 'creature_sub_type'].includes(key)));
    delete host.item_layer_types;
    if (Object.keys(taxonomy).length) host.item_layer_types = taxonomy;
    for (const key of [...metadata.abilityTypes, ...extraAbilities, 'spells_known', 'curses_known', 'spell', 'essence_feat',
        'creature_type', 'creature_sub_type', 'skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod', 'damage_type_resistance',
        'damage_type_immunity', 'damage_type_vulnerability', 'items', 'item_videos', 'charges', 'pre_status_speed_layer_presence',
        'derivative_speed_basis', 'class_arr', 'subclass_arr', 'background_arr', 'lineage_arr', 'creature_type_arr',
        'creature_sub_type_arr', '', '_user_choice_nested_map', '_ft_prepared_spells_loaded',
        ...['body', 'mind', 'soul', 'spirit', 'fortune', 'luck'].flatMap(key => [key, `ancestral_${key}`, `background_${key}`]),
        ...metadata.additionalAbilityTypes.map(row => row.add_ability_name ?? ''), ...Object.keys(host).filter(key => key.endsWith('_class_level'))]) delete host[key];
    const player = host.player_characters as Row; delete player.item_layer_types;
    if (fromBaseRecord(host.encounter_creatures)) delete host.encounter_creatures.computed;
    constructPlayerSpellClustersFromBase(base, metadata, host);
    // The native PC castable bake restores its complete (possibly empty) inventory list.
    host.inventory ??= [];
    host = normalizePlayerClusterValues(host, readers, shell) as Row;
    const display = constructPlayerActionBarsFromBase(base);
    host = Object.fromEntries([...new Set(['encounter_creatures', 'encounter_creatures_spells_prepared', 'position', 'computed', 'player_characters',
        ...Object.keys(display), 'statuses', 'inventory', 'user_choices', ...Object.keys(host)])]
        .filter(key => key in host || key in display).map(key => [key, display[key] ?? host[key]]));
    return normalizeFromBaseCatalog(host, { itemField: readers.itemField, definition,
        rawDefinition: id => rawCatalog[id] ?? null, abilityTypes: metadata.abilityTypes,
        randomTableRows: base.random_table_rows });
}
